import {
  Button,
  Card,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Grid,
  GridColumn,
  GridRow,
  HistoryIcon,
  ImportantIcon,
  ProfileIcon,
} from "@bp/ui-components";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { InfoBox } from "../../../components/InfoBox/InfoBox";
import { MemberList, MemberRow } from "./MemberRow";
import css from "../orgList.module.scss";

// Colocated fragment: the admin-profile shape this view renders.
const OmniOrgView_Profile = graphql(`
  fragment OmniOrgView_Profile on Profile {
    uuid
    firstName
    lastName
    email
    user {
      uuid
    }
    rolesConnection {
      edges {
        properties {
          names
        }
        node {
          __typename
          ... on Organization {
            uuid
          }
        }
      }
    }
  }
`);

const OmniOrgViewQuery = graphql(`
  query OmniOrgView_Query($where: ProfileWhere) {
    profiles(where: $where) {
      ...OmniOrgView_Profile
    }
  }
`);

const MIGRATION_STEPS = [
  "Persönliche Support-Identitäten anlegen",
  "Admin-Rechte pro Person vergeben",
  "Omni-Admin auf read-only setzen",
  "Geteilten Zugang abschalten",
];

const ADMIN_ROLES = new Set(["admin", "owner"]);

type ProfileLike = {
  uuid: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  user?: { uuid?: string } | null;
  rolesConnection?: {
    edges?: ReadonlyArray<{
      properties?: { names?: readonly string[] | null } | null;
      node?: { __typename?: string; uuid?: string } | null;
    }>;
  } | null;
};

function isOrgAdmin(profile: ProfileLike, orgUuid: string): boolean {
  for (const edge of profile.rolesConnection?.edges ?? []) {
    if (edge.node?.__typename === "Organization" && edge.node.uuid === orgUuid) {
      if ((edge.properties?.names ?? []).some((n) => ADMIN_ROLES.has(n.toLowerCase()))) {
        return true;
      }
    }
  }
  return false;
}

export function OmniOrgView({ org }: { org: { uuid: string; name: string } }) {
  const navigate = useNavigate();
  const [{ data }] = useQuery({
    query: OmniOrgViewQuery,
    variables: { where: { organization: { uuid: org.uuid } } },
  });

  const profiles = getFragmentData(OmniOrgView_Profile, data?.profiles ?? []);
  const admins = profiles.filter((p) => isOrgAdmin(p, org.uuid));

  return (
    <div>
      <InfoBox tone="warning" icon={<ImportantIcon style={{ fontSize: 18 }} />}>
        <strong>Geteilter Admin-Zugang — Accountability-Lücke.</strong>
        <div style={{ marginTop: "var(--spacing-2)", color: "var(--color-grey-dark)" }}>
          {org.name}: Profile mit Admin-Rolle erben Admin-Rechte in allen Organisationen. Aktionen
          aus diesem Zugang lassen sich keiner Person zuordnen. Empfehlung: persönliche
          Support-Identitäten statt eines geteilten Kontos.
        </div>
      </InfoBox>

      <Grid useFormGap>
        <GridRow>
          <GridColumn width={6}>
            <Card
              header={{ headline: `Profile mit Admin-Rolle (${admins.length})` }}
              contentPadding="none"
            >
              <div
                style={{
                  fontSize: 13,
                  color: "var(--color-grey)",
                  padding: "12px 16px 4px",
                }}
              >
                Diese Profile erben Admin-Rechte in allen Organisationen.
              </div>
              <MemberList isEmpty={admins.length === 0} emptyTitle="Keine Admin-Profile">
                {admins.map((p) => {
                  const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.email || "—";
                  const userUuid = p.user?.uuid;
                  return (
                    <MemberRow
                      key={p.uuid}
                      onClick={
                        userUuid ? () => navigate(`/users/${userUuid}/orgs/${org.uuid}`) : undefined
                      }
                      status={
                        <ProfileIcon style={{ fontSize: 18, color: "var(--color-warning)" }} />
                      }
                      statusTint="var(--color-warning-lighter)"
                      name={name}
                      sub={p.email}
                      actions={
                        userUuid ? (
                          <Dropdown
                            trigger={
                              <Button
                                hierarchy="tertiary"
                                icon={<DotsHorizontalIcon />}
                                ariaLabel="Aktionen"
                                onClick={(e) => e.stopPropagation()}
                              />
                            }
                          >
                            <DropdownMenu
                              data={[
                                {
                                  label: "Benutzer öffnen",
                                  onClick: () => navigate(`/users/${userUuid}/orgs/${org.uuid}`),
                                },
                              ]}
                            />
                          </Dropdown>
                        ) : undefined
                      }
                    />
                  );
                })}
              </MemberList>
              <div style={{ padding: "12px 16px" }}>
                <Button
                  hierarchy="secondary"
                  height="s"
                  icon={<HistoryIcon />}
                  onClick={() => navigate("/audit")}
                >
                  Aktivität des Zugangs im Audit-Log
                </Button>
              </div>
            </Card>
          </GridColumn>
          <GridColumn width={6}>
            <Card header={{ headline: "Migrationspfad" }}>
              <ol className={css.steps}>
                {MIGRATION_STEPS.map((step, i) => (
                  <li key={step} className={css.stepRow}>
                    <span className={css.stepNum}>{i + 1}</span>
                    <span className={css.stepLabel}>{step}</span>
                  </li>
                ))}
              </ol>
            </Card>
          </GridColumn>
        </GridRow>
      </Grid>
    </div>
  );
}
